import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { formatAuditValue } from '@common/audit-trail/auditTrailFormat';
import { ValuePreview } from './ValuePreviewDialog';

export type ValuePillVariant = 'old' | 'new' | 'removed';

const PILL_CLASSES: Record<ValuePillVariant, string> = {
    old: 'bg-muted text-muted-foreground',
    new: 'bg-[#EDF6E8] text-fg-success new:bg-success-tint',
    removed:
        'bg-[#FBEDED] text-[#A8383A] line-through decoration-[#D89A9B] new:bg-danger-tint new:text-danger-ink new:decoration-danger-ink/40',
};

const VIEW_BUTTON_CLASSES: Record<ValuePillVariant, string> = {
    old: 'text-muted-foreground',
    new: 'text-fg-success',
    removed: 'text-[#A8383A] new:text-danger-ink',
};

export function ValuePill({
    value,
    label,
    variant,
    previewTitle,
    onPreview,
}: {
    value: unknown;
    /** Resolved display name for a reference id; when set it replaces the raw value, which moves to the tooltip. */
    label?: string;
    variant: ValuePillVariant;
    previewTitle: string;
    onPreview: (preview: ValuePreview) => void;
}) {
    const formatted = formatAuditValue(value);
    return (
        <span
            className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-[7px] px-2.5 py-[3px] font-mono text-[12.5px]',
                PILL_CLASSES[variant]
            )}
            title={label ? `${t('audit-trail.id')} ${formatted.display}` : undefined}
        >
            <span className="truncate">{label ?? formatted.display}</span>
            {!label && formatted.expandable && (
                <button
                    type="button"
                    className={cn(
                        'new:text-caption flex-none cursor-pointer font-sans text-[11px] font-semibold underline',
                        VIEW_BUTTON_CLASSES[variant]
                    )}
                    onClick={() => onPreview({ title: previewTitle, content: formatted.full })}
                >
                    {t('audit-trail.view')}
                </button>
            )}
        </span>
    );
}
